import { createUnsafeLinkPath } from './createLinkPath';
import type { NavigateArgs } from './types/NavigateArgsType';
import type { QueryParameters } from './types/NavigateOptions';
import { getCurrentRouterStrategy } from './routerTypeStrategy';

export type BaseNavigateOptions = {
    /**
     * Should replace location state instead of pushing new one.
     */
    replace?: boolean;
    /**
     * Query parameters to append to the path.
     */
    queryParameters?: QueryParameters;
};

export type NavigateOptions<Path extends string> =
    NavigateArgs<Path> extends never
        ? [] | [BaseNavigateOptions]
        : [NavigateArgs<Path> & BaseNavigateOptions];

/**
 * Performs navigation to a specified path without type-safety.
 *
 * @template Path The type of the path string
 * @param {Path} path The path to navigate to
 * @param {NavigateOptions<Path>} options Navigation options including path parameters, query parameters, and navigation behavior
 *
 * @example
 * ```typescript
 * // In a utility function or service
 * function redirectToUserProfile(userId: string) {
 *   unsafeNavigate('/user/:id', { id: userId });
 * }
 *
 * // In an event handler outside of React
 * document.addEventListener('some-global-event', () => {
 *   unsafeNavigate('/event-triggered-page');
 * });
 * ```
 *
 * @remarks
 * Use this function when you need to navigate:
 * - Outside of React.
 * - Prefer using the type-safe navigate(path).
 * - For navigation within React components, prefer using the useNavigate hook.
 */
export const unsafeNavigate = <Path extends string>(
    path: Path,
    ...[options]: NavigateOptions<Path>
): void => {
    const { queryParameters, replace, ...pathParams } = options ?? {};
    const formattedUrl = createUnsafeLinkPath(path, {
        ...(pathParams as NavigateArgs<Path>),
        queryParameters,
    });

    const url = new URL(window.location.href);
    getCurrentRouterStrategy().setUrl(url, formattedUrl);

    if (replace) {
        window.history.replaceState({}, '', url.toString());
    } else {
        window.history.pushState({}, '', url.toString());
    }

    // notify react-router
    window.dispatchEvent(new Event('popstate'));
};

/**
 * Performs navigation to a specified path.
 * Use this function for simple navigations outside of React components or hooks.
 *
 * @param {string} path The path to navigate to
 * @param {NavigateOptions<string>} options Navigation options
 *
 * @remarks
 * Use this function when you need to navigate:
 * - Outside of React.
 * - For navigation within React components, prefer using the useNavigate hook.
 */
export function navigate(path: '/', ...[options]: NavigateOptions<'/'>): void {
    // @ts-expect-error invalid type
    unsafeNavigate(path, options);
}
